<template>
  <a-modal
    v-model:visible="visible"
    :centered="true"
    :footer="null"
    :destroyOnClose="true"
    width="726px"
    :bodyStyle="{ padding: 0 }"
    wrapClassName="revisit_path_modal"
    @cancel="handleCancel"
  >
    <template #title>
      <span class="path_title">回访路径</span>
    </template>

    <div class="revisit_path">
      <!-- 患者信息 -->
      <div class="patient_info">
        <div v-for="(item, index) in infoList.first" :key="item.label" class="info_item">
          <span class="info_label">
            {{ item.label }}
            <span class="info_value">{{ item.value || '--' }}</span>
          </span>
          <span v-if="index < infoList.first.length - 1" class="info_separator"></span>
        </div>
      </div>
      <div class="patient_info">
        <div v-for="(item, index) in infoList.second" :key="item.label" class="info_item">
          <span class="info_label">
            {{ item.label }}
            <span class="info_value">
              <template v-if="Array.isArray(item.value)">
                <span>{{ item.value[0] || '--' }}</span>
                <span class="time_sep">~</span>
                <span
                  class="time_part"
                  :class="{ status_text: [2, 4].includes(data.followupStatus) }"
                >
                  {{ item.value[1] || '--' }}
                </span>
              </template>
              <template v-else>{{ item.value || '--' }}</template>
            </span>
          </span>
          <span v-if="index < infoList.second.length - 1" class="info_separator"></span>
        </div>
      </div>

      <!-- 区块分割线 -->
      <div class="section_divider"></div>

      <!-- 回访路径 Timeline -->
      <div class="timeline_wrap">
        <el-timeline>
          <el-timeline-item
            v-for="(node, index) in pathNodes"
            :key="index"
            :timestamp="node.eventTime"
            placement="top"
          >
            <template #dot>
              <span class="custom_dot"></span>
            </template>

            <div class="node_content">
              <span>
                {{ node.eventContent }}
              </span>
              <span v-if="node.actionType > 0" class="node_action" @click="handleAction(node)">
                {{ node.actionName }}
              </span>
            </div>
          </el-timeline-item>
        </el-timeline>
      </div>
    </div>
  </a-modal>
</template>

<script>
export default { name: 'RevisitPath' }
</script>

<script setup>
import { ref, computed, reactive, defineEmits, defineExpose } from 'vue'
import { getRevisitPath } from '@/api/UserPool/activeRevisit'
import { genderMap } from '@/views/UserPool/activeRevisit/config'

const emit = defineEmits(['action'])

const visible = ref(false)
const actionMap = {
  1: '查看通话记录',
  2: '查看处方详情',
  3: '查看预约单'
}

const data = reactive({})
const record = ref()

const infoList = computed(() => ({
  first: [
    { label: '回访患者：', value: data.patientName },
    { label: 'ID：', value: data.patientId },
    { label: '性别：', value: genderMap[data.gender] },
    { label: '年龄：', value: data.age }
  ],
  second: [
    { label: '回访健管：', value: data.daName },
    {
      label: '回访时间：',
      value: [
        data.followupStartedAt,
        [2, 4].includes(data.followupStatus) ? data.followupStatusName : data.followupCompletedAt
      ]
    }
  ]
}))

const pathNodes = computed(() =>
  (data.pathRecords || []).map(r => ({
    ...r,
    actionName: actionMap[r.actionType]
  }))
)

/**
 * 打开弹窗（对外暴露）
 * @param {Object} [record]
 */
async function initModal(record = {}) {
  try {
    const res = await getRevisitPath({ activeFollowupId: record.id })
    record.value = record

    Object.assign(data, res)
    visible.value = true
  } catch (error) {
    console.error('查询主动回访路径失败:', error)
  }
}

function handleCancel() {
  visible.value = false
}

function handleAction(node) {
  // path.actionType 1查看通话记录 2查看处方详情 3查看预约单
  emit('action', { path: node, record: record.value, pathInfo: data })
}

defineExpose({ initModal })
</script>

<style lang="scss">
// 弹窗样式（teleport 到 body，需用非 scoped 覆盖）
// 用 wrapClassName 限定作用域，避免污染其它弹窗
.revisit_path_modal {
  .ant-modal-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.09);
    padding: 16px 24px;
    margin-bottom: 0;
  }
  .ant-modal-title {
    .path_title {
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
      color: rgba(0, 0, 0, 0.85);
      font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, sans-serif;
    }
  }
  .ant-modal-body {
    padding: 0;
  }
}
</style>

<style scoped lang="scss">
.revisit_path {
  background: #fff;
  display: flex;
  flex-direction: column;
  max-height: 460px;
  overflow: hidden;

  // 患者信息行
  .patient_info {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 13px;
    padding: 24px 24px 0;
    .info_item {
      display: inline-flex;
      align-items: center;
      gap: 13px;
      .info_label {
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        color: #999;

        white-space: nowrap;
      }
      .info_value {
        line-height: 20px;
        color: #333;
      }
      .time_sep {
        margin: 0 4px;
      }
      .status_text {
        color: #999;
      }
      .info_separator {
        display: inline-block;
        width: 1px;
        height: 20px;
        background: #e8e8e8;
      }
    }
    // 最后一项不显示分割线
    .info_item:last-child .info_separator {
      display: none;
    }
  }

  // 区块分割线
  .section_divider {
    height: 1px;
    background: #e8e8e8;
    margin: 24px 24px 0;
  }

  // Timeline 区域
  .timeline_wrap {
    flex: 1;
    overflow-y: auto;
    padding: 16px 24px;
  }
}

// 覆盖 Element Plus Timeline 样式
.timeline_wrap {
  :deep(.el-timeline) {
    padding-left: 0;
    margin: 0;
  }

  :deep(.el-timeline-item) {
    padding-bottom: 24px;
    position: relative;
    &:last-child {
      padding-bottom: 0;
      // 最后一个节点隐藏连线
      .el-timeline-item__tail {
        display: none;
      }
    }
  }

  // 连线：2px #F0F2F5
  :deep(.el-timeline-item__tail) {
    border-left: 2px solid #f0f2f5;
    left: 7px;
    top: 18px;
    height: calc(100% - 12px);
  }

  // 节点容器（自定义 dot 插槽会覆盖默认 node）
  :deep(.el-timeline-item__node--normal) {
    background: transparent;
    left: 0;
    top: 3px;
    width: 16px;
    height: 16px;
  }

  // 自定义圆点：外环 rgba(24,144,255,0.2) + 内点 #1890FF
  .custom_dot {
    display: block;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: rgba(24, 144, 255, 0.2);
    position: relative;
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #1890ff;
    }
  }

  // 时间戳：16px 500 #333
  :deep(.el-timeline-item__timestamp) {
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
    color: #333;
    margin-bottom: 8px;
    padding-top: 0;
  }

  :deep(.el-timeline-item__wrapper) {
    padding-left: 28px;
    top: -3px;
  }

  :deep(.el-timeline-item__content) {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }
}

// 节点内容
.node_content {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.node_action {
  color: #1890ff;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
}
</style>
